import React from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  setModalId,
  usePublicState,
} from '../../../../store/slices/publicSlice';
import CustomModal from '../../../atoms/CustomModal';
import Button from '../../../atoms/Button';
import TitleText from '../../../atoms/TitleText';
import InfoText from '../../../atoms/InfoText';
import { CloseCircle, CloseSquare } from 'iconsax-react';
import { useRejectProductRequestMutation } from '../../../../store/services/products';
import { message } from 'antd';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import z from 'zod';
import FormField from '../../../FormField/FormField';
import { useNavigate } from 'react-router-dom';

type SchemaMeta = {
  reason: string;
};

const REASON_PATTERN = /^[\p{L}\p{N}\p{P}\p{S}\s]*$/u;

export default function RejectProductModal() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { modalId, modalExtraData } = usePublicState();
  const [rejectRequest, { isLoading }] = useRejectProductRequestMutation();
  const navigate = useNavigate();

  const isModalOpened = modalId === 'REJECT_PRODUCT_MODAL';
  const productId = (modalExtraData as any)?.productId;

  const schema = z.object({
    reason: z
      .string()
      .trim()
      .min(1, t('components.validations.requiredField', 'Required'))
      .min(20, t('components.validations.minLength', { value: 20 }))
      .max(500, t('components.validations.maxLength', { value: 500 }))
      .regex(REASON_PATTERN, t('home.reasonValidation')),
  });

  const methods = useForm<SchemaMeta>({
    resolver: zodResolver(schema),
    mode: 'all',
    defaultValues: { reason: '' },
  });

  const onClose = () => {
    dispatch(setModalId(null));
    methods.reset();
  };

  const onSubmit = async (data: SchemaMeta) => {
    if (!productId) return;
    try {
      await rejectRequest({
        productId,
        body: { reason: data.reason.trim() },
      }).unwrap();
      message.success(t('home.REJECTED', 'Rejected successfully'));
      onClose();
      navigate('/products');
    } catch (error) {
      const err = error as { data?: { errors?: { reason?: string } } };
      message.error(err?.data?.errors?.reason || t('home.somethingWentWrong'));
    }
  };

  return (
    <CustomModal
      isOpen={isModalOpened}
      onClose={onClose}
      title=""
      closable={false}
      centered
      width={600}
    >
      <div
        className="flex flex-col text-left space-y-4 pb-4 pt-6"
        data-testid="reject-modal-content"
      >
        <div
          className="flex flex-col items-center text-center space-y-4"
          data-testid="reject-modal-header"
        >
          <div
            className="w-[44px] h-[44px] bg-red-50 dark:bg-red-900/20 rounded-[12px] flex items-center justify-center"
            data-testid="reject-modal-icon-wrapper"
          >
            <CloseSquare
              size={20}
              variant="Linear"
              className="!text-[#CC2936]"
            />
          </div>

          <TitleText
            text={t('home.rejectProduct')}
            className="!font-bold !text-[#111928] dark:!text-white !leading-none !text-2xl"
          />

          <InfoText
            className="!text-[#637381] dark:!text-gray-400 !font-normal text-center !mt-[4px]"
            text={t('home.rejectRequestDescription')}
          />
        </div>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onSubmit)}
            className="space-y-4 w-full"
            data-testid="reject-modal-form"
          >
            <FormField
              id="reason"
              name="reason"
              type="textarea"
              label={t('home.reason')}
              placeholder={t('home.rejectProductPlaceholder')}
              rows={3}
              minLength={20}
              maxLength={500}
              showCount
              helperText={t('Minimum character 20 and the max. 500')}
              labelClassName="!text-base !font-medium !leading-6 !text-[#111928]"
              className="!rounded-xl"
            />

            <div
              className="flex items-center justify-center !gap-6 w-full pt-9"
              data-testid="reject-modal-buttons"
            >
              <Button
                variant="outline"
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="flex items-center justify-center !rounded-[12px] !border-1 !border-[#090909] dark:!border-gray-600 !text-[#090909] dark:!text-gray-200 !font-semibold w-[113px] !h-[46px] !px-[24px] !py-[12px] !gap-[8px]"
                size="md"
                data-testid="reject-modal-cancel-btn"
              >
                <span className="!text-base !leading-[22px] !font-semibold">
                  {t('home.cancel')}
                </span>
              </Button>
              <Button
                size="md"
                type="submit"
                disabled={isLoading}
                icon={
                  <CloseCircle size={20} variant="Linear" color="#FFFFFF" />
                }
                className="!rounded-xl !font-semibold !px-6 !w-[253px] !py-3 !bg-[#CC2936] !text-white hover:!bg-[#DC2626] border-0"
                data-testid="reject-modal-submit-btn"
              >
                {t('home.reject')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </div>
    </CustomModal>
  );
}
